import type { DistributorSummary, DistributorUpsertRequest } from '../dtos/distributor';

export class Distributor {
  readonly id: number;
  readonly firstName: string;
  readonly lastName: string;
  readonly emailAddress: string;
  readonly phoneNumber: string;
  readonly streetAddress: string;
  readonly city: string;
  readonly stateAbbr: string;
  readonly postalCode: string;
  readonly merchantID: string;
  readonly status: 'Active' | 'Inactive';
  readonly macTruckSignupID: number;
  readonly merchantName: string;
  readonly division: string;
  readonly region: string;
  readonly district: string;
  readonly ownerMerchantID: string;
  readonly ownerEmail: string;
  readonly ownerPhone: string;
  readonly statusDate: string;

  constructor(params: Partial<Distributor> & { id?: number }) {
    this.id = params.id ?? 0;
    this.firstName = params.firstName ?? '';
    this.lastName = params.lastName ?? '';
    this.emailAddress = params.emailAddress ?? '';
    this.phoneNumber = params.phoneNumber ?? '';
    this.streetAddress = params.streetAddress ?? '';
    this.city = params.city ?? '';
    this.stateAbbr = params.stateAbbr ?? '';
    this.postalCode = params.postalCode ?? '';
    this.merchantID = params.merchantID ?? '';
    this.status = params.status ?? 'Active';
    this.macTruckSignupID = params.macTruckSignupID ?? 0;
    this.merchantName = params.merchantName ?? '';
    this.division = params.division ?? '';
    this.region = params.region ?? '';
    this.district = params.district ?? '';
    this.ownerMerchantID = params.ownerMerchantID ?? '';
    this.ownerEmail = params.ownerEmail ?? '';
    this.ownerPhone = params.ownerPhone ?? '';
    this.statusDate = params.statusDate ?? '';
  }

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  get address(): string {
    return `${this.streetAddress}, ${this.city}, ${this.stateAbbr} ${this.postalCode}`.trim();
  }

  toUpsertRequest(): DistributorUpsertRequest {
    return {
      firstName: this.firstName,
      lastName: this.lastName,
      emailAddress: this.emailAddress,
      phoneNumber: this.phoneNumber,
      streetAddress: this.streetAddress,
      city: this.city,
      stateAbbr: this.stateAbbr,
      postalCode: this.postalCode,
      merchantID: this.merchantID,
      status: this.status,
      macTruckSignupID: this.macTruckSignupID,
      merchantName: this.merchantName,
      division: this.division,
      region: this.region,
      district: this.district,
      ownerMerchantID: this.ownerMerchantID,
      ownerEmail: this.ownerEmail,
      ownerPhone: this.ownerPhone || undefined,
    };
  }
}

export const DistributorMapper = {
  fromDto(dto: DistributorSummary): Distributor {
    return new Distributor({
      id: dto.id,
      firstName: dto.firstName,
      lastName: dto.lastName,
      emailAddress: dto.emailAddress,
      phoneNumber: dto.phoneNumber,
      streetAddress: dto.streetAddress,
      city: dto.city,
      stateAbbr: dto.stateAbbr,
      postalCode: dto.postalCode,
      merchantID: dto.merchantID,
      status: dto.status,
      macTruckSignupID: dto.macTruckSignupID,
      merchantName: dto.merchantName,
      division: dto.division,
      region: dto.region,
      district: dto.district,
      ownerMerchantID: dto.ownerMerchantID,
      ownerEmail: dto.ownerEmail,
      ownerPhone: dto.ownerPhone,
      statusDate: dto.statusDate,
    });
  },

  fromDtoList(dtos: DistributorSummary[]): Distributor[] {
    return (dtos ?? []).map((dto) => DistributorMapper.fromDto(dto));
  },
};
